/**
 * Define the Route List items
 */
import React from 'react';
import NoPermission from '../Modules/Core/NoPermission/NoPermission';
import { faCircle } from '@fortawesome/free-regular-svg-icons';

import Login from '../Modules/Login/Pages/Login';
import Users from '../Modules/User/Pages/Users';
import AddUser from '../Modules/User/Pages/AddUser';
import generateRolloutIcon from '../assets/images/generate-rollout.svg';
import webhookLogsIcon from '../assets/images/webhook_logs.svg';
import ObjectChangeLogIcon from '../assets/images/object_change_log.svg';
import AffectedFileIcon from '../assets/images/affected_file.svg';
import {
  faBug,
  faBox,
  faGear,
  faUmbrella,
  faChartPie,
} from '@fortawesome/free-solid-svg-icons';
import Account from '../Modules/User/Pages/Account';
import Projects from '../Modules/Projects/Pages/Projects';
import CreateRollout from '../Modules/Projects/Pages/CreateRollout';
import RolloutList from '../Modules/RolloutList/Pages/RolloutList';
import ObjectChangelog from '../Modules/ObjectChangelog/Pages/ObjectChangelog';
import AffectedFiles from '../Modules/AffectedFiles/Pages/AffectedFiles';
import WebHookLogs from '../Modules/WebHookLogs/Pages/WebHookLogs';
import Setting from '../Modules/Settings/Pages/Setting';
import SocialLoginRedirect from '../Modules/Login/Components/SocialLoginRedirect';
import LoginCallback from '../Modules/Login/Pages/LoginCallback';
import GenerateRollout from '../Modules/Rollout/Pages/GenerateRollout';
import Dashboard from '../Modules/Dashboard/Pages/Dashboard';
import Splash from '../Modules/Login/Pages/Splash';
import Authorization from '../Modules/Authorization/Pages/Authorization';

const RoutesItemsList = [
  {
    text: '',
    icon: '',
    route: '/login',
    element: <Splash />,
    layouts: 'EmptyLayout',
    is_drawer_item: 'false',
    page_title: 'Login',
    isauth: false,
    is_mobile: 'true',
  },
  {
    ['data-expanded']: false,
    text: 'Users',
    icon: '',
    route: '/users',
    element: <Users />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    page_title: 'Users',
    page_eyebrow: 'Administration',
    page_description:
      'Manage application users, access, and profile-level administration.',
    isauth: true,
    authPermissions: ['authorization.manage'],
    is_mobile: 'true',
    iconsvg: faBox,
    icon_type: 'FontAwesomeIcon',
  },
  {
    text: 'AddUsers',
    icon: 'k-i-minus',
    iconsvg: faCircle,
    route: '/add-users',
    element: <AddUser />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    page_title: 'Add Users',
    page_eyebrow: 'Administration',
    page_description:
      'Create and onboard application users with a consistent admin workflow.',
    isauth: true,
    authPermissions: ['authorization.manage'],
    is_mobile: 'true',
    icon_type: 'FontAwesomeIcon',
  },
  {
    text: 'Dashboard',
    icon: 'k-i-graph',
    id: 16,
    page_title: 'Dashboard',
    element: <Dashboard />,
    route: '/',
    layouts: 'TileLayout',
    iconReact: 'LuLayoutDashboard',
    is_drawer_item: 'true',
    page_eyebrow: 'Operations Overview',
    page_description:
      'Monitor rollout volume, affected repositories, issue trends, and activity from one workspace.',
    isauth: true,
    authPermissions: ['dashboard.view'],
    is_mobile: 'true',
  },
  {
    id: 3,
    text: 'Projects',
    icon: '',
    iconReact: 'LuSquareKanbon',
    route: '/projects',
    element: <Projects />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Project List',
    page_eyebrow: 'Release Operations',
    page_description:
      'Browse deployment-ready projects, inspect rollout activity, and launch new packages.',
    isauth: true,
    authPermissions: ['projects.view'],
    is_mobile: 'true',
  },
  {
    id: 5,
    text: 'Bugzilla',
    icon: '',
    route: '/bugzilla',
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    page_title: 'Bugzilla',
    isauth: true,
    is_mobile: 'true',
    iconsvg: faBug,
    icon_type: 'FontAwesomeIcon',
  },
  {
    id: 6,
    ['data-expanded']: false,
    text: 'Utilities',
    icon: '',
    iconReact: 'LuBlocks',
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Utilities',
    page_eyebrow: 'Shared Tools',
    page_description:
      'Access operational utilities for file analysis, webhook auditing, and rollout generation.',
    isauth: true,
    is_mobile: 'true',
  },
  {
    id: 8,
    parentid: 6,
    text: 'Affected Files',
    icon: '',
    iconReact: 'LuFileDiff',
    route: '/affectedfiles',
    element: <AffectedFiles />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Affected Files',
    page_eyebrow: 'Utilities',
    page_description:
      'Search and review changed files across repositories with standardized filters and results.',
    isauth: true,
    is_mobile: 'true',
  },
  {
    id: 9,
    parentid: 6,
    text: 'Object Changelog',
    icon: '',
    iconReact: 'LuFileClock',
    route: '/objectchangelog',
    element: <ObjectChangelog />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Object Change Logs',
    page_eyebrow: 'Utilities',
    page_description:
      'Inspect object-level change history with sortable data and quick drill-down access.',
    isauth: true,
    is_mobile: 'true',
  },
  {
    id: 10,
    parentid: 6,
    text: 'Webhook Logs',
    iconReact: 'LuActivity',
    route: 'webhooklogs',
    element: <WebHookLogs />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Webhook Logs',
    page_eyebrow: 'Utilities',
    page_description:
      'Audit inbound webhook activity, payload details, and delivery outcomes.',
    isauth: true,
    authPermissions: ['webhooks.view'],
    is_mobile: 'true',
  },
  {
    id: 18,
    parentid: 6,
    text: 'Generate Rollout',
    iconReact: 'LuPackagePlus',
    route: '/generate-rollout',
    element: <GenerateRollout />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Generate Rollout',
    page_eyebrow: 'Utilities',
    page_description:
      'Upload an archive and generate rollout artifacts using the standardized processing flow.',
    isauth: true,
    authPermissions: ['rollouts.generate'],
    is_mobile: 'true',
  },
  {
    footer_start: true,
    is_drawer_item: 'true',
  },
  {
    id: 21,
    text: 'Access Control',
    icon: '',
    iconReact: 'LuShield',
    route: '/authorization',
    element: <Authorization />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Access Control',
    page_eyebrow: 'Administration',
    page_description:
      'Manage roles, permissions, and user assignments for the rollout workspace.',
    isauth: true,
    authPermissions: ['authorization.manage'],
    is_mobile: 'true',
  },
  {
    id: 7,
    text: 'Settings',
    icon: '',
    iconReact: 'LuSettings',
    route: '/settings',
    element: <Setting />,
    layouts: 'CardLayout',
    is_drawer_item: 'true',
    page_title: 'Settings',
    page_eyebrow: 'Platform Controls',
    page_description:
      'Configure providers, authentication modes, storage, and environment behavior.',
    isauth: true,
    authAnyPermissions: ['configurations.view', 'configurations.manage'],
    is_mobile: 'true',
  },
  {
    id: 11,
    text: 'My Account',
    icon: '',
    route: '/account',
    element: <Account />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    page_title: 'My Account',
    page_eyebrow: 'Personal Workspace',
    page_description:
      'Update your profile, contact details, and integration tokens.',
    isauth: true,
    authPermissions: ['profile.update'],
    is_mobile: 'true',
  },
  {
    id: 12,
    parentid: 3,
    text: 'Create Rollout',
    icon: '',
    route: '/projects/create-rollout',
    element: <CreateRollout />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    go_back: true,
    go_back_link: '/projects',
    page_title: 'Create Rollout',
    page_eyebrow: 'Release Builder',
    page_description:
      'Choose a project, select a version, and move through a guided rollout creation flow.',
    isauth: true,
    authPermissions: ['rollouts.create'],
    is_mobile: 'true',
  },
  {
    id: 20,
    text: 'Create Rollout',
    icon: '',
    route: '/createrollout/:projectId/:versionId/:action/:major?/:name?',
    element: <CreateRollout />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    go_back: true,
    go_back_link: '/projects',
    page_title: 'Create Rollout',
    page_eyebrow: 'Release Builder',
    page_description:
      'Define rollout metadata, attach bug references, and prepare a release-ready package.',
    isauth: true,
    authPermissions: ['rollouts.create'],
    is_mobile: 'true',
  },
  {
    id: 13,
    text: 'Rollout',
    icon: '',
    route: '/rolloutList/:projectId/:versionId',
    element: <RolloutList />,
    layouts: 'CardLayout',
    is_drawer_item: 'false',
    page_title: 'Rollout List',
    page_eyebrow: 'Release Operations',
    page_description:
      'Review rollout history, recreate Rollouts, and manage generated Rollouts.',
    isauth: true,
    authPermissions: ['rollouts.view'],
    is_mobile: 'true',
  },
  {
    id: 14,
    text: 'Social Login Redirect',
    icon: '',
    route: 'social/login/callback',
    element: <SocialLoginRedirect />,
    layouts: 'EmptyLayout',
    is_drawer_item: 'false',
    page_title: 'Social Login Redirect',
    isauth: false,
    is_mobile: 'true',
  },
  {
    id: 19,
    text: 'Login Callback',
    icon: '',
    route: '/callback/smart-oidc',
    element: <LoginCallback />,
    layouts: 'EmptyLayout',
    is_drawer_item: 'false',
    page_title: 'Login Callback',
    isauth: false,
    is_mobile: 'true',
  },
  {
    text: 'No Permission',
    route: '/no-permission',
    element: <NoPermission />,
    layouts: 'TileLayout',
    is_drawer_item: 'false',
    page_title: 'No Permission',
    page_eyebrow: 'Access Control',
    page_description:
      'You do not currently have permission to access this workspace.',
    is_mobile: 'false',
  }
];

const RoutesItems = RoutesItemsList;

export default RoutesItems;
